import React from 'react';
import {styles, ViewRow, ViewLine} from './InvoicePDFLayout';
import {Text, View} from '@react-pdf/renderer';
import {ViewColumn} from '../../../../../components/initial-plan/pdf/layout/PDFLayout';
import Util from '../../../../../forecast-app/shared/util/util';
import {IntlShape} from 'react-intl';
import type {InvoicePDFDownloadModal_viewer$data as InvoicePDFDownloadModal_viewer} from './__generated__/InvoicePDFDownloadModal_viewer.graphql';

const DESCRIPTION_WIDTH = '54%';
const QUANTITY_WIDTH = '10%';
const UNIT_PRICE_WIDTH = '18%';
const AMOUNT_WIDTH = '18%';

type InvoicePDFHeaderProps = {
	invoiceData: InvoicePDFDownloadModal_viewer['invoice'];
	intl: IntlShape;
};

const InvoicePDFHeader = ({invoiceData, intl}: InvoicePDFHeaderProps): JSX.Element => {
	const initialInvoiceLines = invoiceData?.entries?.edges?.map(e => ({
		...e?.node,
		projectId: e?.node?.project ? e?.node?.project?.id : null,
	}));

	const invoiceAmounts = Util.getInvoiceTotals(initialInvoiceLines);

	const subTotalString = Util.getFormattedNumberWithCurrency(
		Util.GetCurrencySymbol(invoiceData?.currency),
		invoiceAmounts.subtotal,
		intl
	);

	const discount = Util.getFormattedNumberWithCurrency(
		Util.GetCurrencySymbol(invoiceData?.currency),
		invoiceAmounts.discount,
		intl
	);

	const tax = Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(invoiceData?.currency), invoiceAmounts.tax, intl);

	const totalDue = Util.getFormattedNumberWithCurrency(
		Util.GetCurrencySymbol(invoiceData?.currency),
		invoiceAmounts.total,
		intl
	);

	return (
		<View style={{paddingTop: '51px'}}>
			<ViewLine style={{alignItems: 'center', backgroundColor: '#D9D9D9', height: '24px'}}>
				<Text style={[styles.textMain, {width: DESCRIPTION_WIDTH}]}>
					{intl.formatMessage({id: 'common.description'})}
				</Text>
				<Text style={[styles.textTableRight, {width: QUANTITY_WIDTH}]}>
					{intl.formatMessage({id: 'common.quantity'})}
				</Text>
				<Text style={[styles.textTableRight, {width: UNIT_PRICE_WIDTH}]}>
					{intl.formatMessage({id: 'common.unit_price'})}
				</Text>
				<Text style={[styles.textTableRight, {width: AMOUNT_WIDTH}]}>{intl.formatMessage({id: 'common.amount'})}</Text>
			</ViewLine>
			{invoiceData?.entries?.edges &&
				invoiceData?.entries?.edges?.length > 0 &&
				invoiceData?.entries?.edges?.map(entry => {
					const unitPriceString = Util.getFormattedNumberWithCurrency(
						Util.GetCurrencySymbol(invoiceData.currency),
						entry?.node?.unitPrice,
						intl
					);

					const totalString = Util.getFormattedNumberWithCurrency(
						Util.GetCurrencySymbol(invoiceData?.currency),
						Util.getInvoiceEntryAmount(
							entry?.node?.unitPrice,
							entry?.node?.quantity,
							entry?.node?.discount,
							entry?.node?.tax
						),
						intl
					);

					return (
						<ViewLine key={entry?.node?.id}>
							<Text
								style={[styles.textMain, {width: DESCRIPTION_WIDTH, paddingTop: '4px', paddingBottom: '4px'}]}
							>
								{entry?.node?.description}
							</Text>
							<Text style={[styles.textTableRight, {width: QUANTITY_WIDTH}]}>{entry?.node?.quantity}</Text>
							<Text style={[styles.textTableRight, {width: UNIT_PRICE_WIDTH}]}>{unitPriceString}</Text>
							<Text style={[styles.textTableRight, {width: AMOUNT_WIDTH}]}>{totalString}</Text>
						</ViewLine>
					);
				})}
			<ViewRow style={{justifyContent: 'flex-end', paddingTop: '8px', gap: '20px', paddingRight: '20px'}}>
				<ViewColumn>
					<Text style={styles.textSummary}>{intl.formatMessage({id: 'common.subtotal'})}:</Text>
					{invoiceAmounts.discount !== 0 ? (
						<Text style={styles.textSummary}>
							{intl.formatMessage({id: 'project_section.xero_modal_table_discount'})}:
						</Text>
					) : null}
					<Text style={styles.textSummary}>{intl.formatMessage({id: 'common.tax'})}:</Text>
					<Text style={styles.textSummaryBold}>Balance due:</Text>
				</ViewColumn>
				<ViewColumn style={{alignItems: 'flex-end'}}>
					<Text style={styles.textSummary}>{subTotalString}</Text>
					{invoiceAmounts.discount !== 0 ? <Text style={styles.textSummary}>{discount}</Text> : null}
					<Text style={styles.textSummary}>{tax}</Text>
					<Text style={styles.textSummaryBold}>{totalDue}</Text>
				</ViewColumn>
			</ViewRow>
		</View>
	);
};

export default InvoicePDFHeader;
